import React, { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import DrawIcon from "@mui/icons-material/Draw";
import SignatureCanvas from "react-signature-canvas";
import axios from "axios";
import {
  API_post_file_upload_with_eagerly_generated_presigned_url,
  API_post_user_signature,
} from "../../../constants";
import { getDefaultAxiosConfig, postRightworkAPI } from "../requests/utilities/requests";

// Fonts offered for a typed signature. The key is stored as typed_font and used
// to render the signature consistently wherever it's applied.
export const TYPED_SIGNATURE_FONTS = [
  { key: "cursive", label: "Casual", fontFamily: "'Brush Script MT', cursive" },
  { key: "serif", label: "Formal", fontFamily: "Georgia, 'Times New Roman', serif" },
  { key: "mono", label: "Print", fontFamily: "'Courier New', monospace" },
];

export function fontFamilyForKey(key) {
  const found = TYPED_SIGNATURE_FONTS.find((f) => f.key === key);
  return found ? found.fontFamily : TYPED_SIGNATURE_FONTS[0].fontFamily;
}

// Rasterize a typed signature in its chosen font to a transparent PNG data URL,
// so it can be embedded into a generated/signed PDF (pdf-lib's standard fonts
// can't render the script/serif/mono styles). Rendered at 3x for crispness.
// Returns null on failure.
export function renderTypedSignatureToPng(name, fontKey) {
  try {
    const SCALE = 3;
    const fontSize = 44;
    const fontFamily = fontFamilyForKey(fontKey);
    const measure = document.createElement("canvas").getContext("2d");
    measure.font = `${fontSize}px ${fontFamily}`;
    const pad = 8;
    const w = Math.ceil(measure.measureText(name).width) + pad * 2;
    const h = fontSize + pad * 2;

    const canvas = document.createElement("canvas");
    canvas.width = w * SCALE;
    canvas.height = h * SCALE;
    const ctx = canvas.getContext("2d");
    ctx.scale(SCALE, SCALE);
    ctx.font = `${fontSize}px ${fontFamily}`;
    ctx.fillStyle = "#1a1a1a";
    ctx.textBaseline = "middle";
    ctx.fillText(name, pad, h / 2);
    return canvas.toDataURL("image/png");
  } catch (e) {
    return null;
  }
}

// Shared violet focus-border treatment for LMS form inputs.
const inputSx = {
  backgroundColor: "white",
  borderRadius: "8px",
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#6d28d9",
  },
};

// Lets a user adopt a reusable signature (typed or drawn) once, then reuse it.
// On success calls onAdopted(userSignature) with the persisted record.
function AdoptSignatureModal(props) {
  const { open, handleClose, homeCompany, onAdopted } = props;

  const [tab, setTab] = useState("typed");
  const [typedName, setTypedName] = useState("");
  const [typedFont, setTypedFont] = useState(TYPED_SIGNATURE_FONTS[0].key);
  const [isSaving, setIsSaving] = useState(false);
  const sigCanvas = useRef(null);

  useEffect(() => {
    if (open) {
      setTab("typed");
      setTypedName("");
      setTypedFont(TYPED_SIGNATURE_FONTS[0].key);
      setIsSaving(false);
    }
  }, [open]);

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 520,
    maxWidth: "94%",
    bgcolor: "background.paper",
    borderRadius: "12px",
    boxShadow: 24,
    p: 0,
  };

  // Upload the drawn signature PNG via the standard presigned 3-step flow.
  const uploadDrawnImage = async (dataUrl) => {
    const blob = await (await fetch(dataUrl)).blob();
    const file = new File([blob], "signature.png", { type: "image/png" });

    const formData = new FormData();
    formData.append("company_id", homeCompany);
    formData.append("file_name", file.name);
    formData.append("type", "lms_user_signature_image");

    const axiosConfig = await getDefaultAxiosConfig();
    const res = await axios.post(
      API_post_file_upload_with_eagerly_generated_presigned_url,
      formData,
      { headers: { ...axiosConfig.headers, "Content-Type": "multipart/form-data" } }
    );
    const fileUUID = res?.data?.redux?.payload?.uuid;
    const presignedUrl = res?.data?.redux?.payload?.presigned_url;

    await axios.put(presignedUrl, file, { headers: { "Content-Type": file.type } });
    return fileUUID;
  };

  const handleSave = async () => {
    if (isSaving) return;

    try {
      setIsSaving(true);
      let params;

      if (tab === "typed") {
        if (!typedName.trim()) {
          toast.error("Please type your name");
          setIsSaving(false);
          return;
        }
        params = {
          company_id: homeCompany,
          method: "typed",
          typed_name: typedName.trim(),
          typed_font: typedFont,
        };
      } else {
        if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
          toast.error("Please draw your signature");
          setIsSaving(false);
          return;
        }
        const dataUrl = sigCanvas.current.toDataURL("image/png");
        const imageFileUuid = await uploadDrawnImage(dataUrl);
        params = {
          company_id: homeCompany,
          method: "drawn",
          image_file_uuid: imageFileUuid,
        };
      }

      const response = await postRightworkAPI(API_post_user_signature, params);
      const userSignature = response?.data?.redux?.payload?.user_signature;
      if (onAdopted) onAdopted(userSignature);
      handleClose();
    } catch (error) {
      console.error("Error saving signature:", error);
      toast.error("Failed to save signature. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose} aria-labelledby="adopt-signature-modal">
      <Box sx={style}>
        <div className="bg-white rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-slate-900">Adopt your signature</h3>
            <button onClick={handleClose} className="text-slate-500 hover:text-slate-700">
              <CloseIcon />
            </button>
          </div>

          <p className="text-sm text-slate-600 mb-4">
            Create your signature once. You can reuse it on every document.
          </p>

          {/* Tab selector */}
          <div className="flex gap-3 mb-4">
            <button
              onClick={() => setTab("typed")}
              className={`flex-1 px-4 py-3 rounded-lg border flex items-center justify-center gap-2 transition-all ${
                tab === "typed"
                  ? "bg-violet-700 text-white border-violet-700"
                  : "bg-white text-slate-700 border-slate-300 hover:border-violet-700"
              }`}
            >
              <KeyboardIcon style={{ fontSize: 18 }} />
              <span className="font-medium">Type</span>
            </button>
            <button
              onClick={() => setTab("drawn")}
              className={`flex-1 px-4 py-3 rounded-lg border flex items-center justify-center gap-2 transition-all ${
                tab === "drawn"
                  ? "bg-violet-700 text-white border-violet-700"
                  : "bg-white text-slate-700 border-slate-300 hover:border-violet-700"
              }`}
            >
              <DrawIcon style={{ fontSize: 18 }} />
              <span className="font-medium">Draw</span>
            </button>
          </div>

          {tab === "typed" ? (
            <div>
              <TextField
                placeholder="Type your full name"
                fullWidth
                variant="outlined"
                size="small"
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                InputProps={{ sx: { ...inputSx, height: "45px" } }}
                sx={{ mb: 1.5 }}
              />
              {/* Live preview */}
              <div className="border border-slate-200 rounded-lg h-24 flex items-center justify-center mb-3 bg-slate-50">
                <span
                  style={{ fontFamily: fontFamilyForKey(typedFont), fontSize: 32 }}
                  className="text-slate-900"
                >
                  {typedName || "Your signature"}
                </span>
              </div>
              <div className="flex gap-2">
                {TYPED_SIGNATURE_FONTS.map((f) => (
                  <button
                    key={f.key}
                    onClick={() => setTypedFont(f.key)}
                    style={{ fontFamily: f.fontFamily }}
                    className={`flex-1 px-3 py-2 rounded-lg border text-lg ${
                      typedFont === f.key
                        ? "border-violet-700 bg-violet-50 text-violet-900"
                        : "border-slate-300 text-slate-700 hover:border-violet-400"
                    }`}
                  >
                    {typedName ? typedName.split(" ")[0] : f.label}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div>
              <div className="border border-slate-300 rounded-lg mb-3 flex justify-center">
                <SignatureCanvas
                  ref={sigCanvas}
                  canvasProps={{ width: 450, height: 180, className: "signature-canvas" }}
                />
              </div>
              <button
                onClick={() => sigCanvas.current?.clear()}
                className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg font-medium transition-colors"
              >
                Clear
              </button>
            </div>
          )}

          <div className="flex justify-end gap-2 mt-6">
            <button
              onClick={handleClose}
              className="px-4 py-2.5 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className={`px-4 py-2.5 rounded-lg font-medium transition-colors ${
                isSaving
                  ? "bg-slate-300 text-slate-500 cursor-not-allowed"
                  : "bg-violet-600 hover:bg-violet-700 text-white"
              }`}
            >
              {isSaving ? "Saving..." : "Adopt & Continue"}
            </button>
          </div>
        </div>
      </Box>
    </Modal>
  );
}

export default AdoptSignatureModal;
